import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Paper,
  Stack,
  Tooltip
} from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import SavingsIcon from '@mui/icons-material/Savings';
import { collection, addDoc, getDocs, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { matchesSearch } from '../utils/searchText';
import { allocateIslemNo } from '../utils/islemNoService';
import { findMusteriByTypedName } from '../utils/islemMusteriMatch';
import { attachFinanceSnapshotToIslem } from '../utils/finance/attachFinanceSnapshot';
import { usePriceContext } from '../context/PriceContext';

/**
 * Mevcut kasa (mevcutkasa.js) nakit: Genel satırlarda !isTL için
 *  - Giriş / Alış / İade / Değişim → kasa TL azalır (nakit çıkar)
 *  - Satış / Çıkış → kasa TL artar (nakit girer)
 * Banka (banka.js): havaleEft > 0 için Satış/Giriş bankayı artırır, Alış/Çıkış/… azaltır.
 *
 * Bankaya nakit çık: kasa ↓ bank ↑ → Giriş + nakit + havaleEft (aynı tutar)
 * Bankadan kasaya: kasa ↑ bank ↓ → Çıkış + nakit + havaleEft
 *
 * Ürün hızlı işlemleri: yeniislem ile aynı kategori/ürün/islemTuru/adet veya gram/ödeme alanları.
 */

const SARRAFIYE_PRESETS = [
  { id: 'eski_ziynet_ceyrek_satis', btn: 'Eski çeyrek satış', kategori: 'Sarrafiye', urun: 'Eski Ziynet Çeyrek', islemTuru: 'Satış' },
  { id: 'eski_ziynet_ceyrek_alis', btn: 'Eski çeyrek alış', kategori: 'Sarrafiye', urun: 'Eski Ziynet Çeyrek', islemTuru: 'Alış' },
  { id: 'eski_ziynet_yarim_satis', btn: 'Eski yarım satış', kategori: 'Sarrafiye', urun: 'Eski Ziynet Yarım', islemTuru: 'Satış' },
  { id: 'eski_ziynet_yarim_alis', btn: 'Eski yarım alış', kategori: 'Sarrafiye', urun: 'Eski Ziynet Yarım', islemTuru: 'Alış' },
  { id: 'eski_ziynet_tam_satis', btn: 'Eski tam satış', kategori: 'Sarrafiye', urun: 'Eski Ziynet Tam', islemTuru: 'Satış' },
  { id: 'eski_ziynet_tam_alis', btn: 'Eski tam alış', kategori: 'Sarrafiye', urun: 'Eski Ziynet Tam', islemTuru: 'Alış' },
  { id: 'yeni_ziynet_ceyrek_satis', btn: 'Yeni çeyrek satış', kategori: 'Sarrafiye', urun: 'Yeni Ziynet Çeyrek', islemTuru: 'Satış' },
  { id: 'yeni_ziynet_ceyrek_alis', btn: 'Yeni çeyrek alış', kategori: 'Sarrafiye', urun: 'Yeni Ziynet Çeyrek', islemTuru: 'Alış' },
  { id: 'yeni_ziynet_yarim_satis', btn: 'Yeni yarım satış', kategori: 'Sarrafiye', urun: 'Yeni Ziynet Yarım', islemTuru: 'Satış' },
  { id: 'yeni_ziynet_yarim_alis', btn: 'Yeni yarım alış', kategori: 'Sarrafiye', urun: 'Yeni Ziynet Yarım', islemTuru: 'Alış' },
  { id: 'yeni_ziynet_tam_satis', btn: 'Yeni tam satış', kategori: 'Sarrafiye', urun: 'Yeni Ziynet Tam', islemTuru: 'Satış' },
  { id: 'yeni_ziynet_tam_alis', btn: 'Yeni tam alış', kategori: 'Sarrafiye', urun: 'Yeni Ziynet Tam', islemTuru: 'Alış' },
  { id: 'cumhuriyet_ata_satis', btn: 'Cumhuriyet ata satış', kategori: 'Sarrafiye', urun: 'Cumhuriyet Ata', islemTuru: 'Satış' },
  { id: 'cumhuriyet_ata_alis', btn: 'Cumhuriyet ata alış', kategori: 'Sarrafiye', urun: 'Cumhuriyet Ata', islemTuru: 'Alış' }
];

const GRAM_PRESETS = [
  { id: 'bir_gram_24_satis', btn: '1 gram 24 ayar satış', kategori: 'Gram Altın', urun: '1 Gram 24 Ayar', islemTuru: 'Satış' },
  { id: 'bir_gram_24_alis', btn: '1 gram 24 ayar alış', kategori: 'Gram Altın', urun: '1 Gram 24 Ayar', islemTuru: 'Alış' }
];

const TAKI_PRESETS = [
  { id: 'taki_22_bilezik_satis', btn: '22 ayar bilezik satış', kategori: 'Takı Ürün', urun: '22 Ayar Bilezik', islemTuru: 'Satış' },
  { id: 'taki_22_bilezik_alis', btn: '22 ayar bilezik alış', kategori: 'Takı Ürün', urun: '22 Ayar Bilezik', islemTuru: 'Alış' },
  { id: 'taki_14_bilezik_satis', btn: '14 ayar bilezik satış', kategori: 'Takı Ürün', urun: '14 Ayar Bilezik', islemTuru: 'Satış' },
  { id: 'taki_14_bilezik_alis', btn: '14 ayar bilezik alış', kategori: 'Takı Ürün', urun: '14 Ayar Bilezik', islemTuru: 'Alış' }
];

const DOVIZ_PRESETS = [
  { id: 'usd_satis', btn: 'USD satış', kategori: 'Döviz', urun: 'USD - Amerikan Doları', islemTuru: 'Satış' },
  { id: 'usd_alis', btn: 'USD alış', kategori: 'Döviz', urun: 'USD - Amerikan Doları', islemTuru: 'Alış' },
  { id: 'eur_satis', btn: 'EUR satış', kategori: 'Döviz', urun: 'EUR - Euro', islemTuru: 'Satış' },
  { id: 'eur_alis', btn: 'EUR alış', kategori: 'Döviz', urun: 'EUR - Euro', islemTuru: 'Alış' }
];

const HURDA_PRESETS = [
  { id: 'hurda_24_ayar_alis', btn: '24 ayar alış', kategori: 'Hurda', urun: '24 Ayar Hurda', islemTuru: 'Alış' },
  { id: 'hurda_22_ayar_bilezik_alis', btn: '22 ayar bilezik alış', kategori: 'Hurda', urun: '22 Ayar Bilezik Hurda', islemTuru: 'Alış' },
  { id: 'hurda_22_ayar_alis', btn: '22 ayar alış', kategori: 'Hurda', urun: '22 Ayar Hurda', islemTuru: 'Alış' },
  { id: 'hurda_21_ayar_alis', btn: '21 ayar alış', kategori: 'Hurda', urun: '21 Ayar Hurda', islemTuru: 'Alış' },
  { id: 'hurda_18_ayar_alis', btn: '18 ayar alış', kategori: 'Hurda', urun: '18 Ayar Hurda', islemTuru: 'Alış' },
  { id: 'hurda_14_ayar_alis', btn: '14 ayar alış', kategori: 'Hurda', urun: '14 Ayar Hurda', islemTuru: 'Alış' },
  { id: 'hurda_8_ayar_alis', btn: '8 ayar alış', kategori: 'Hurda', urun: '8 Ayar Hurda', islemTuru: 'Alış' }
];

function presetUsesGramOnly(preset) {
  return preset?.kategori === 'Hurda';
}

function presetUsesTakı(preset) {
  return preset?.kategori === 'Takı Ürün';
}

function toTitleCaseTr(s) {
  if (!s || typeof s !== 'string') return '';
  return s
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => {
      const lower = w.toLocaleLowerCase('tr-TR');
      const first = lower.charAt(0).toLocaleUpperCase('tr-TR');
      return first + lower.slice(1);
    })
    .join(' ');
}

/** Sade kurumsal palet — az renk, net hiyerarşi */
const THEME = {
  title: '#111827',
  muted: '#6b7280',
  columnBg: '#ffffff',
  columnBorder: '#e5e7eb',
  headerBg: '#f9fafb',
  headerText: '#374151',
  headerLine: '#e5e7eb',
  satisBg: '#111827',
  satisText: '#ffffff',
  satisHover: '#1f2937',
  alisBg: '#ffffff',
  alisText: '#374151',
  alisBorder: '#d1d5db',
  alisHover: '#f9fafb',
  bankOutBg: '#111827',
  bankOutText: '#ffffff',
  bankInBg: '#ffffff',
  bankInText: '#374151',
  bankInBorder: '#d1d5db'
};

const sxQuickBtn = (kind) => {
  const isFilled = kind === 'satis' || kind === 'bankOut';
  if (isFilled) {
    return {
      textTransform: 'none',
      fontWeight: 600,
      fontSize: '0.72rem',
      letterSpacing: '0.01em',
      lineHeight: 1.25,
      borderRadius: '6px',
      border: '1px solid #111827',
      bgcolor: THEME.satisBg,
      color: THEME.satisText,
      boxShadow: 'none',
      width: '100%',
      minHeight: 36,
      height: 'auto',
      px: 1,
      py: 0.65,
      whiteSpace: 'normal',
      justifyContent: 'center',
      transition: 'background-color 0.12s ease',
      '&:hover': { bgcolor: THEME.satisHover, boxShadow: 'none' },
      '& .MuiButton-startIcon': { mr: 0.5, ml: 0 }
    };
  }
  return {
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.72rem',
    letterSpacing: '0.01em',
    lineHeight: 1.25,
    borderRadius: '6px',
    border: `1px solid ${THEME.alisBorder}`,
    bgcolor: THEME.alisBg,
    color: THEME.alisText,
    boxShadow: 'none',
    width: '100%',
    minHeight: 36,
    height: 'auto',
    px: 1,
    py: 0.65,
    whiteSpace: 'normal',
    justifyContent: 'center',
    transition: 'background-color 0.12s ease, border-color 0.12s ease',
    '&:hover': {
      bgcolor: THEME.alisHover,
      borderColor: '#9ca3af',
      boxShadow: 'none'
    },
    '& .MuiButton-startIcon': { mr: 0.5, ml: 0 }
  };
};

const sxNumberInputNoSpinners = {
  '& input[type=number]': { MozAppearance: 'textfield' },
  '& input[type=number]::-webkit-outer-spin-button': { WebkitAppearance: 'none', margin: 0 },
  '& input[type=number]::-webkit-inner-spin-button': { WebkitAppearance: 'none', margin: 0 }
};

function parseMoney(v) {
  const n = parseFloat(String(v ?? '').replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
}

function splitMusteriAdi(trimmed) {
  const spaceIndex = trimmed.lastIndexOf(' ');
  if (spaceIndex === -1) return { isim: trimmed, soyisim: '' };
  return {
    isim: trimmed.substring(0, spaceIndex).trim(),
    soyisim: trimmed.substring(spaceIndex + 1).trim()
  };
}

async function generateIslemNo(sube) {
  return allocateIslemNo(sube);
}

function CategoryColumn({ title, children, wide }) {
  return (
    <Paper
      elevation={0}
      className={`hizli-islem-col${wide ? ' is-wide' : ''}`}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        height: '100%',
        overflow: 'hidden',
        bgcolor: THEME.columnBg,
        border: `1px solid ${THEME.columnBorder}`,
        borderRadius: '8px',
        flex: wide ? { md: '1.4 1 0' } : { md: '1 1 0' }
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          px: 1.25,
          py: 0.9,
          bgcolor: THEME.headerBg,
          borderBottom: `1px solid ${THEME.headerLine}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Typography
          sx={{
            m: 0,
            fontSize: '0.7rem',
            fontWeight: 700,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: THEME.headerText,
            lineHeight: 1.2,
            textAlign: 'center'
          }}
        >
          {title}
        </Typography>
      </Box>
      <Box
        className="hizli-islem-col-body"
        sx={{
          flex: 1,
          minHeight: 0,
          p: 0.85,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.6,
          overflow: 'auto'
        }}
      >
        {children}
      </Box>
    </Paper>
  );
}

function PresetStack({ presets, onOpen, paired }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: paired ? '1fr 1fr' : '1fr',
        gap: 0.6,
        alignContent: 'start'
      }}
    >
      {presets.map((p) => (
        <Button
          key={p.id}
          disableElevation
          onClick={() => onOpen(p)}
          sx={sxQuickBtn(p.islemTuru === 'Satış' ? 'satis' : 'alis')}
        >
          {toTitleCaseTr(p.btn)}
        </Button>
      ))}
    </Box>
  );
}

function HizliIslem({ sube }) {
  const { prices } = usePriceContext();
  /** @type {null | { type: 'bank', dir: 'toBank' | 'fromBank' } | { type: 'urun', preset: object }} */
  const [dialog, setDialog] = useState(null);

  const [amount, setAmount] = useState('');
  const [musteriAdi, setMusteriAdi] = useState('');
  const [selectedMusteri, setSelectedMusteri] = useState(null);
  const [musteriListesi, setMusteriListesi] = useState([]);
  const [filteredMusteri, setFilteredMusteri] = useState([]);
  const [showMusteriDropdown, setShowMusteriDropdown] = useState(false);

  const [adet, setAdet] = useState('');
  const [gram, setGram] = useState('');
  const [nakit, setNakit] = useState('');
  const [havaleEft, setHavaleEft] = useState('');
  const [krediKarti, setKrediKarti] = useState('');

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [hizliKayitBasariDialogAcik, setHizliKayitBasariDialogAcik] = useState(false);
  const [hizliKayitBasariDetay, setHizliKayitBasariDetay] = useState('');

  const odemeOnizleme = useMemo(
    () => parseMoney(nakit) + parseMoney(havaleEft) + parseMoney(krediKarti),
    [nakit, havaleEft, krediKarti]
  );

  const resetFormlar = useCallback(() => {
    setAmount('');
    setMusteriAdi('');
    setSelectedMusteri(null);
    setFilteredMusteri([]);
    setShowMusteriDropdown(false);
    setAdet('');
    setGram('');
    setNakit('');
    setHavaleEft('');
    setKrediKarti('');
  }, []);

  useEffect(() => {
    if (!sube) return;
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDocs(collection(db, 'subeler', sube, 'musteriler'));
        if (cancelled) return;
        const list = snap.docs.map((d) => ({
          id: d.id,
          ad: `${d.data().isim || ''} ${d.data().soyisim || ''}`.trim(),
          isim: d.data().isim || '',
          soyisim: d.data().soyisim || '',
          ...d.data()
        }));
        setMusteriListesi(list);
      } catch (err) {
        console.error('Hızlı işlem: müşteri listesi alınamadı:', err);
        if (!cancelled) setMusteriListesi([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sube]);

  useEffect(() => {
    if (musteriAdi) {
      const filtered = musteriListesi.filter((m) => matchesSearch(musteriAdi, m.ad));
      setFilteredMusteri(filtered);
      // Seçim yapıldıktan sonra input'a yazılan isim menüyü yeniden açmasın
      setShowMusteriDropdown(!selectedMusteri && filtered.length > 0 && musteriAdi.length > 0);
    } else {
      setFilteredMusteri([]);
      setShowMusteriDropdown(false);
    }
  }, [musteriAdi, musteriListesi, selectedMusteri]);

  useEffect(() => {
    if (!hizliKayitBasariDialogAcik) return undefined;
    const t = setTimeout(() => {
      setHizliKayitBasariDialogAcik(false);
      setHizliKayitBasariDetay('');
    }, 2000);
    return () => clearTimeout(t);
  }, [hizliKayitBasariDialogAcik]);

  const closeDialog = useCallback(() => {
    if (saving) return;
    setDialog(null);
    resetFormlar();
    setError('');
  }, [saving, resetFormlar]);

  const submitBankTransfer = useCallback(async () => {
    if (!sube) {
      setError('Şube bilgisi yok.');
      return;
    }
    if (dialog?.type !== 'bank') return;

    const amt = parseMoney(amount);
    if (amt <= 0) {
      setError('Geçerli bir tutar girin.');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const islemNo = await generateIslemNo(sube);
      const toBank = dialog.dir === 'toBank';
      const islemTuru = toBank ? 'Giriş' : 'Çıkış';
      const aciklama = toBank
        ? `${toTitleCaseTr('hızlı işlem')}: ${toTitleCaseTr('bankaya nakit çıkışı')}`
        : `${toTitleCaseTr('hızlı işlem')}: ${toTitleCaseTr('bankadan kasaya nakit girişi')}`;

      const islemData = {
        islemNo,
        kategori: 'Genel',
        urun: '',
        islemTuru,
        musteri: { isim: 'Kasa-Banka', soyisim: 'Transfer' },
        firma: null,
        barkod: null,
        gram: 0.01,
        adet: 0,
        urunBilgisi: aciklama,
        islemAciklamasi: aciklama,
        hasFiyati: amt,
        bakiye: 0,
        nakit: amt,
        havaleEft: amt,
        krediKarti: 0,
        toplamTutar: parseFloat(amt.toFixed(2)),
        listeFiyati: 0,
        hasEsdeger: 0,
        teslimatDurumu: 'Teslim Edildi',
        planlananTeslimTarihi: null,
        islemFisi: null,
        timestamp: serverTimestamp(),
        sube,
        isMulti: false,
        hizliIslemTipi: toBank ? 'bankaya_nakit_cik' : 'bankadan_kasa_nakit'
      };

      await addDoc(collection(db, 'subeler', sube, 'islemler'), islemData);

      setHizliKayitBasariDetay(
        `${aciklama} · No: ${islemNo} · ${amt.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} TL`
      );
      setHizliKayitBasariDialogAcik(true);
      setDialog(null);
      resetFormlar();
      setError('');
    } catch (err) {
      console.error(err);
      setError(err.message || 'Kayıt başarısız.');
    } finally {
      setSaving(false);
    }
  }, [sube, dialog, amount, resetFormlar]);

  const submitUrunHizli = useCallback(async () => {
    if (!sube) {
      setError('Şube bilgisi yok.');
      return;
    }
    if (dialog?.type !== 'urun') return;

    const preset = dialog.preset;
    const trimmedMusteri = musteriAdi.trim();
    if (!trimmedMusteri) {
      setError('Müşteri adı girin.');
      return;
    }

    const usesGramOnly = presetUsesGramOnly(preset);
    const usesTaki = presetUsesTakı(preset);
    let adetInt = 0;
    let gramVal = 0;

    if (usesGramOnly) {
      gramVal = parseFloat(String(gram).trim().replace(',', '.'));
      if (!Number.isFinite(gramVal) || gramVal <= 0) {
        setError('Geçerli bir gram girin.');
        return;
      }
    } else if (usesTaki) {
      adetInt = parseInt(String(adet).trim(), 10);
      gramVal = parseFloat(String(gram).trim().replace(',', '.'));
      if (!Number.isFinite(adetInt) || adetInt <= 0) {
        setError('Geçerli bir adet girin.');
        return;
      }
      if (!Number.isFinite(gramVal) || gramVal <= 0) {
        setError('Geçerli bir gram girin.');
        return;
      }
    } else {
      adetInt = parseInt(String(adet).trim(), 10);
      if (!Number.isFinite(adetInt) || adetInt <= 0) {
        setError('Geçerli bir adet girin.');
        return;
      }
    }

    const nakitN = parseMoney(nakit);
    const havaleN = parseMoney(havaleEft);
    const kartN = parseMoney(krediKarti);
    const toplamTutar = parseFloat((nakitN + havaleN + kartN).toFixed(2));

    if (toplamTutar <= 0) {
      setError('Nakit, havale/EFT veya kredi kartı tutarlarından en az biri girilmeli.');
      return;
    }

    const miktar = usesGramOnly ? gramVal : usesTaki ? adetInt * gramVal : adetInt;
    const hasFiyati = Math.max(0.01, parseFloat((toplamTutar / miktar).toFixed(2)));
    const aciklama = `${toTitleCaseTr('hızlı işlem')}: ${toTitleCaseTr(preset.btn)}`;

    setSaving(true);
    setError('');

    try {
      const islemNo = await generateIslemNo(sube);
      const matchedMusteri = selectedMusteri || findMusteriByTypedName(musteriListesi, trimmedMusteri);
      const musteri = matchedMusteri
        ? {
            id: matchedMusteri.id,
            isim: matchedMusteri.isim,
            soyisim: matchedMusteri.soyisim || ''
          }
        : splitMusteriAdi(trimmedMusteri);

      const islemData = {
        islemNo,
        kategori: preset.kategori,
        urun: preset.urun,
        islemTuru: preset.islemTuru,
        musteri,
        firma: null,
        barkod: null,
        gram: usesGramOnly || usesTaki ? gramVal : 0,
        adet: usesGramOnly ? 0 : adetInt,
        urunBilgisi: aciklama,
        islemAciklamasi: aciklama,
        hasFiyati,
        bakiye: 0,
        nakit: parseFloat(nakitN.toFixed(2)),
        havaleEft: parseFloat(havaleN.toFixed(2)),
        krediKarti: parseFloat(kartN.toFixed(2)),
        toplamTutar,
        listeFiyati: 0,
        hasEsdeger: 0,
        teslimatDurumu: 'Teslim Edildi',
        planlananTeslimTarihi: null,
        islemFisi: null,
        timestamp: serverTimestamp(),
        sube,
        isMulti: false,
        hizliIslemTipi: preset.id
      };

      let kayit = islemData;
      try {
        kayit = await attachFinanceSnapshotToIslem(islemData, { sube, prices });
      } catch (snapErr) {
        console.warn('financeSnapshot yazılamadı', snapErr);
      }

      await addDoc(collection(db, 'subeler', sube, 'islemler'), kayit);

      setHizliKayitBasariDetay(
        `${aciklama} · No: ${islemNo} · ${toplamTutar.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} TL`
      );
      setHizliKayitBasariDialogAcik(true);
      setDialog(null);
      resetFormlar();
      setError('');
    } catch (err) {
      console.error(err);
      setError(err.message || 'Kayıt başarısız.');
    } finally {
      setSaving(false);
    }
  }, [sube, dialog, musteriAdi, selectedMusteri, musteriListesi, adet, gram, nakit, havaleEft, krediKarti, resetFormlar, prices]);

  const handleDialogSave = useCallback(() => {
    if (dialog?.type === 'bank') return submitBankTransfer();
    if (dialog?.type === 'urun') return submitUrunHizli();
  }, [dialog, submitBankTransfer, submitUrunHizli]);

  const openUrunDialog = useCallback(
    (preset) => {
      setDialog({ type: 'urun', preset });
      setError('');
      resetFormlar();
    },
    [resetFormlar]
  );

  const bilgiMetni =
    'Sık yapılan hareketleri yeni işlem ekranına girmeden kaydedersiniz. Kasa ve bankaya aynı kurallarla yansır.';

  const rawDialogTitle =
    dialog?.type === 'bank'
      ? dialog.dir === 'toBank'
        ? 'bankaya nakit çık'
        : 'bankadan kasaya nakit gir'
      : dialog?.type === 'urun'
        ? dialog.preset.btn
        : '';
  const displayDialogTitle = rawDialogTitle ? toTitleCaseTr(rawDialogTitle) : '';
  const activePreset = dialog?.type === 'urun' ? dialog.preset : null;
  const showGramOnly = activePreset && presetUsesGramOnly(activePreset);
  const showTakiFields = activePreset && presetUsesTakı(activePreset);

  return (
    <Box
      className="hizli-islem-page"
      sx={{
        p: { xs: 0.75, sm: 1 },
        height: { sm: '100%' },
        maxHeight: { sm: '100%' },
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        gap: 0.85,
        overflow: { xs: 'auto', sm: 'hidden' },
        fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
        bgcolor: 'transparent'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'baseline',
          gap: 1.25,
          flexShrink: 0,
          minWidth: 0,
          px: 0.25
        }}
      >
        <Typography
          sx={{
            fontWeight: 800,
            color: THEME.title,
            fontSize: { xs: '1rem', sm: '1.1rem' },
            m: 0,
            flexShrink: 0,
            letterSpacing: '0.02em'
          }}
        >
          Hızlı işlem
        </Typography>
        <Tooltip title={bilgiMetni} arrow placement="bottom" enterDelay={400}>
          <Typography
            component="span"
            sx={{
              flex: 1,
              minWidth: 0,
              color: THEME.muted,
              fontSize: '0.75rem',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            {bilgiMetni}
          </Typography>
        </Tooltip>
      </Box>

      {error && !dialog && (
        <Alert severity="error" sx={{ flexShrink: 0, py: 0.5 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Her kategori bir sütun */}
      <Box
        className="hizli-islem-cols"
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 0.85,
          alignItems: 'stretch'
        }}
      >
        <CategoryColumn title="Kasa · Banka" wide>
          <Tooltip title="Kasadaki nakit azalır, banka bakiyesi artar." arrow>
            <Button
              disableElevation
              startIcon={<AccountBalanceIcon sx={{ fontSize: 15 }} />}
              onClick={() => {
                setDialog({ type: 'bank', dir: 'toBank' });
                setError('');
                resetFormlar();
              }}
              sx={sxQuickBtn('bankOut')}
            >
              Bankaya Nakit Çık
            </Button>
          </Tooltip>
          <Tooltip title="Banka bakiyesi azalır, kasadaki nakit artar." arrow>
            <Button
              disableElevation
              startIcon={<SavingsIcon sx={{ fontSize: 15 }} />}
              onClick={() => {
                setDialog({ type: 'bank', dir: 'fromBank' });
                setError('');
                resetFormlar();
              }}
              sx={sxQuickBtn('bankIn')}
            >
              Bankadan Kasaya Nakit Gir
            </Button>
          </Tooltip>
        </CategoryColumn>

        <CategoryColumn title="Sarrafiye" wide>
          <PresetStack presets={SARRAFIYE_PRESETS} onOpen={openUrunDialog} paired />
        </CategoryColumn>

        <CategoryColumn title="Gram Altın">
          <PresetStack presets={GRAM_PRESETS} onOpen={openUrunDialog} />
        </CategoryColumn>

        <CategoryColumn title="Takı">
          <PresetStack presets={TAKI_PRESETS} onOpen={openUrunDialog} />
        </CategoryColumn>

        <CategoryColumn title="Döviz">
          <PresetStack presets={DOVIZ_PRESETS} onOpen={openUrunDialog} />
        </CategoryColumn>

        <CategoryColumn title="Hurda">
          <PresetStack presets={HURDA_PRESETS} onOpen={openUrunDialog} />
        </CategoryColumn>
      </Box>

      <Dialog
        open={!!dialog}
        onClose={closeDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          elevation: 0,
          sx: {
            borderRadius: '12px',
            overflow: 'hidden',
            bgcolor: '#fff',
            border: `1px solid ${THEME.columnBorder}`,
            boxShadow: '0 16px 48px rgba(22, 27, 34, 0.18)',
            m: { xs: 0.75, sm: 2 },
            width: { xs: 'min(calc(100vw - 16px), 360px)', sm: 'auto' },
            maxWidth: { xs: 'min(calc(100vw - 16px), 360px)', sm: '560px' }
          }
        }}
      >
        <DialogTitle
          sx={{
            m: 0,
            py: 1.5,
            px: 2.5,
            bgcolor: THEME.headerBg,
            color: THEME.title,
            fontWeight: 700,
            fontSize: '0.95rem',
            borderBottom: `1px solid ${THEME.columnBorder}`
          }}
        >
          {displayDialogTitle}
        </DialogTitle>
        <DialogContent sx={{ px: 2.5, pt: 2, pb: 1.5 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 1.5, borderRadius: '8px' }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          <Box sx={{ p: 1.5, borderRadius: '10px', border: `1px solid ${THEME.columnBorder}`, bgcolor: THEME.columnBg }}>
            {dialog?.type === 'bank' && (
              <TextField
                autoFocus
                fullWidth
                label="Tutar (TL)"
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                margin="dense"
                inputProps={{ min: 0, step: '0.01' }}
                sx={{
                  ...sxNumberInputNoSpinners,
                  '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: '#fff' }
                }}
              />
            )}

            {dialog?.type === 'urun' && (
              <Stack spacing={1.5}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: 1.25,
                    alignItems: 'flex-start'
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0, position: 'relative', width: '100%' }}>
                    <TextField
                      autoFocus
                      fullWidth
                      label="Müşteri Adı"
                      placeholder="Ad Soyad"
                      value={musteriAdi}
                      onChange={(e) => {
                        setMusteriAdi(e.target.value);
                        setSelectedMusteri(null);
                      }}
                      margin="dense"
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: '#fff' } }}
                    />
                    {showMusteriDropdown && filteredMusteri.length > 0 && (
                      <Paper
                        elevation={6}
                        sx={{
                          position: 'absolute',
                          left: 0,
                          right: 0,
                          top: '100%',
                          mt: 0.5,
                          maxHeight: 180,
                          overflow: 'auto',
                          zIndex: 2000,
                          borderRadius: '8px',
                          border: `1px solid ${THEME.columnBorder}`
                        }}
                      >
                        {filteredMusteri.map((m) => (
                          <Box
                            key={m.id}
                            component="button"
                            type="button"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => {
                              setMusteriAdi(m.ad);
                              setSelectedMusteri({ id: m.id, isim: m.isim, soyisim: m.soyisim || '' });
                              setShowMusteriDropdown(false);
                            }}
                            sx={{
                              display: 'block',
                              width: '100%',
                              textAlign: 'left',
                              border: 'none',
                              background: 'transparent',
                              cursor: 'pointer',
                              px: 1.5,
                              py: 1,
                              borderBottom: '1px solid #f1f5f9',
                              '&:last-of-type': { borderBottom: 'none' },
                              '&:hover': { bgcolor: '#f8fafc' }
                            }}
                          >
                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.82rem' }}>
                              {m.ad}
                            </Typography>
                          </Box>
                        ))}
                      </Paper>
                    )}
                  </Box>

                  {showGramOnly ? (
                    <TextField
                      fullWidth
                      label="Gram (gr)"
                      type="number"
                      value={gram}
                      onChange={(e) => setGram(e.target.value)}
                      margin="dense"
                      inputProps={{ min: 0.01, step: '0.01' }}
                      sx={{
                        width: { xs: '100%', sm: 140 },
                        flexShrink: 0,
                        ...sxNumberInputNoSpinners,
                        '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: '#fff' }
                      }}
                    />
                  ) : showTakiFields ? (
                    <>
                      <TextField
                        fullWidth
                        label="Adet"
                        type="number"
                        value={adet}
                        onChange={(e) => setAdet(e.target.value)}
                        margin="dense"
                        inputProps={{ min: 1, step: 1 }}
                        sx={{
                          width: { xs: '100%', sm: 100 },
                          flexShrink: 0,
                          ...sxNumberInputNoSpinners,
                          '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: '#fff' }
                        }}
                      />
                      <TextField
                        fullWidth
                        label="Gram (gr)"
                        type="number"
                        value={gram}
                        onChange={(e) => setGram(e.target.value)}
                        margin="dense"
                        inputProps={{ min: 0.01, step: '0.01' }}
                        sx={{
                          width: { xs: '100%', sm: 120 },
                          flexShrink: 0,
                          ...sxNumberInputNoSpinners,
                          '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: '#fff' }
                        }}
                      />
                    </>
                  ) : (
                    <TextField
                      fullWidth
                      label="Adet"
                      type="number"
                      value={adet}
                      onChange={(e) => setAdet(e.target.value)}
                      margin="dense"
                      inputProps={{ min: 1, step: 1 }}
                      sx={{
                        width: { xs: '100%', sm: 110 },
                        flexShrink: 0,
                        ...sxNumberInputNoSpinners,
                        '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: '#fff' }
                      }}
                    />
                  )}
                </Box>

                <Box>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      color: THEME.muted,
                      letterSpacing: '0.06em',
                      display: 'block',
                      mb: 0.75,
                      fontSize: '0.68rem',
                      textTransform: 'uppercase'
                    }}
                  >
                    Tutar dağılımı
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1 }}>
                    <TextField
                      label="Nakit (TL)"
                      type="number"
                      value={nakit}
                      onChange={(e) => setNakit(e.target.value)}
                      margin="dense"
                      size="small"
                      inputProps={{ min: 0, step: '0.01' }}
                      sx={{
                        flex: 1,
                        ...sxNumberInputNoSpinners,
                        '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: '#fff' }
                      }}
                    />
                    <TextField
                      label="Havale / EFT (TL)"
                      type="number"
                      value={havaleEft}
                      onChange={(e) => setHavaleEft(e.target.value)}
                      margin="dense"
                      size="small"
                      inputProps={{ min: 0, step: '0.01' }}
                      sx={{
                        flex: 1,
                        ...sxNumberInputNoSpinners,
                        '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: '#fff' }
                      }}
                    />
                    <TextField
                      label="Kredi Kartı (TL)"
                      type="number"
                      value={krediKarti}
                      onChange={(e) => setKrediKarti(e.target.value)}
                      margin="dense"
                      size="small"
                      inputProps={{ min: 0, step: '0.01' }}
                      sx={{
                        flex: 1,
                        ...sxNumberInputNoSpinners,
                        '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: '#fff' }
                      }}
                    />
                  </Box>
                </Box>

                <Box
                  sx={{
                    py: 1,
                    px: 1.5,
                    borderRadius: '8px',
                    bgcolor: THEME.headerBg,
                    border: `1px solid ${THEME.columnBorder}`
                  }}
                >
                  <Typography sx={{ fontWeight: 600, fontSize: '0.82rem', color: THEME.muted }}>
                    Toplam:{' '}
                    <Box component="span" sx={{ color: THEME.title, fontWeight: 800, fontSize: '0.95rem' }}>
                      {odemeOnizleme.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} TL
                    </Box>
                  </Typography>
                </Box>
              </Stack>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2, pt: 0, gap: 1 }}>
          <Button
            onClick={closeDialog}
            disabled={saving}
            sx={{ color: THEME.muted, fontWeight: 600, textTransform: 'none', borderRadius: '8px' }}
          >
            İptal
          </Button>
          <Button
            onClick={handleDialogSave}
            variant="contained"
            disableElevation
            disabled={saving}
            sx={{
              px: 2.5,
              py: 0.9,
              borderRadius: '6px',
              fontWeight: 700,
              textTransform: 'none',
              bgcolor: THEME.satisBg,
              color: THEME.satisText,
              '&:hover': { bgcolor: THEME.satisHover }
            }}
          >
            {saving ? 'Kaydediliyor…' : 'Kaydet'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={hizliKayitBasariDialogAcik}
        onClose={() => {}}
        disableEscapeKeyDown
        slotProps={{
          backdrop: { sx: { backgroundColor: 'rgba(17, 24, 39, 0.4)' } }
        }}
        PaperProps={{
          elevation: 8,
          sx: {
            borderRadius: '10px',
            minWidth: { xs: 0, sm: 420 },
            maxWidth: { xs: 300, sm: 480 },
            width: { xs: 'min(calc(100vw - 24px), 300px)', sm: 'auto' },
            textAlign: 'center',
            overflow: 'hidden',
            bgcolor: '#ffffff',
            border: `1px solid ${THEME.columnBorder}`
          }
        }}
      >
        <DialogContent sx={{ py: { xs: 2.5, sm: 4 }, px: { xs: 2, sm: 4 } }}>
          <Typography sx={{ fontWeight: 800, color: THEME.title, mb: 1, fontSize: { xs: '1.05rem', sm: '1.25rem' } }}>
            Yeni hızlı işlem kaydedildi
          </Typography>
          <Typography sx={{ fontWeight: 600, color: THEME.muted, fontSize: { xs: '0.85rem', sm: '0.95rem' }, mb: hizliKayitBasariDetay ? 1 : 0 }}>
            İşlem başarıyla kaydedildi.
          </Typography>
          {hizliKayitBasariDetay ? (
            <Typography sx={{ color: THEME.title, fontWeight: 600, fontSize: '0.82rem', wordBreak: 'break-word' }}>
              {hizliKayitBasariDetay}
            </Typography>
          ) : null}
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default HizliIslem;
